import { generatePath } from 'react-router-dom';
import { AuthenticatedPath } from '@shared/constants/routes.constants';
import type { SentimentDto, SentimentType } from '@shared/services/apiService/apiService';
import { CALL_DETAIL_TABS, CallDetailTab } from '../model/callDetail.types';

const INVALID_DATE = null;

export const shortenId = (value: string, visibleCharacters = 4): string => {
  if (!value || value.length <= visibleCharacters * 2 + 3) return value;

  return `${value.slice(0, visibleCharacters)}...${value.slice(-visibleCharacters)}`;
};

export const maskCustomerIdentifier = (
  value?: string | null,
  visibleCharacters = 4,
): string | null => {
  if (!value) return null;
  if (value.length <= visibleCharacters * 2 + 3) return value;

  return `${value.slice(0, visibleCharacters)}...${value.slice(-visibleCharacters)}`;
};

export const formatLocalDateTime = (value?: string | Date | null): string | null => {
  if (!value) return INVALID_DATE;

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return INVALID_DATE;

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
    hour12: true,
  }).format(date);
};

export const getElapsedSeconds = (
  startedAt?: string | Date | null,
  endedAt?: string | Date | null,
): number | null => {
  if (!startedAt || !endedAt) return null;

  const started = new Date(startedAt).getTime();
  const ended = new Date(endedAt).getTime();
  if (Number.isNaN(started) || Number.isNaN(ended)) return null;

  return Math.max(0, Math.floor((ended - started) / 1000));
};

export const formatDuration = (seconds: number | null): string | null => {
  if (seconds == null || !Number.isFinite(seconds)) return null;

  const normalizedSeconds = Math.max(0, Math.floor(seconds));
  const hours = String(Math.floor(normalizedSeconds / 3600)).padStart(2, '0');
  const minutes = String(Math.floor((normalizedSeconds % 3600) / 60)).padStart(2, '0');
  const remainingSeconds = String(normalizedSeconds % 60).padStart(2, '0');

  return `${hours}:${minutes}:${remainingSeconds}`;
};

export const getCallSentimentLabel = (sentiments?: SentimentDto[] | null): SentimentType | null => {
  if (!sentiments?.length) return null;

  const scores = sentiments.flatMap((sentiment) => sentiment.sentimentScores);
  if (!scores.length) return null;

  const total = scores.reduce((sum, score) => {
    if (score.label === 'positive') return sum + score.score;
    if (score.label === 'negative') return sum - score.score;
    return sum;
  }, 0);

  if (total > 0) return 'positive';
  if (total < 0) return 'negative';
  return 'neutral';
};

export const getTranscriptSeekOffset = (
  messageCreatedAt?: string | Date | null,
  callStartedAt?: string | Date | null,
  audioDuration?: number | null,
): number | null => {
  if (!messageCreatedAt || !callStartedAt) return null;

  const messageTime = new Date(messageCreatedAt).getTime();
  const callStartTime = new Date(callStartedAt).getTime();
  if (Number.isNaN(messageTime) || Number.isNaN(callStartTime)) return null;

  const offset = Math.max(0, (messageTime - callStartTime) / 1000);
  if (audioDuration == null || !Number.isFinite(audioDuration)) return offset;

  return Math.min(offset, Math.max(0, audioDuration));
};

export const parseCallDetailTab = (value?: string | null): CallDetailTab =>
  value === CALL_DETAIL_TABS.ANALYSIS ? CALL_DETAIL_TABS.ANALYSIS : CALL_DETAIL_TABS.TRANSCRIPT;

export const getCallDetailBackPath = (campaignId?: string | null): string =>
  campaignId
    ? generatePath(AuthenticatedPath.EDIT_CAMPAIGN_V2, { campaignId })
    : AuthenticatedPath.CAMPAIGN;
